import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Modal } from 'antd';
import { UserDetailBody } from './UserDetailBody';

interface UserDetailModalCtx {
  /** 사용자 상세 다이얼로그를 연다. */
  openUser: (userId: string) => void;
}

const Ctx = createContext<UserDetailModalCtx | null>(null);

/** 어디서든 사용자 상세 모달을 열 수 있는 훅. UserDetailModalProvider 하위에서만 사용. */
export function useUserDetailModal(): UserDetailModalCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useUserDetailModal must be used within UserDetailModalProvider');
  }
  return ctx;
}

/**
 * 앱 전역에 사용자 상세 모달을 제공한다.
 * 인증 영역(AdminLayout) 상위에 한 번만 감싸고, 각 페이지는 useUserDetailModal().openUser(id) 로 연다.
 */
export function UserDetailModalProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);

  const openUser = useCallback((id: string) => setUserId(id), []);
  const close = useCallback(() => setUserId(null), []);

  return (
    <Ctx.Provider value={{ openUser }}>
      {children}
      <Modal
        open={!!userId}
        onCancel={close}
        footer={null}
        width={960}
        destroyOnClose
        title="사용자 상세"
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        {userId && <UserDetailBody userId={userId} />}
      </Modal>
    </Ctx.Provider>
  );
}
